import { capitalize } from 'lodash';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';

import type {
  ButtonItem,
  HeadlineItem,
  ImageItem,
  SublineItem,
  TemplateWithButtonFrame,
} from 'hb-builder/components/BuilderComponents/block/Strips/types/items';
import type { StyleChildProps } from 'hb-builder/components/BuilderComponents/block/Strips/types/props';
import AccordionTitle from 'hb-builder/components/common/SettingsLibrary/SettingsDecoration/AccordionTitle';

import Button from './Button';
import FieldsDrawer from './FieldsDrawer';
import GlobalSettings from './GlobalSettings';
import Headline from './Headline';
import Subline from './Subline';

const Style = ({ item, onChange, permissions }) => {
  const stripsTemplateSlider = item.children[0];

  const [firstStripItem] = stripsTemplateSlider.children;

  const [, groupLines, firstButton] = firstStripItem.children;
  const [firstHeadline, firstSubline] = groupLines.children;

  const stripsItems = stripsTemplateSlider.children.reduce(
    (acc, stripsItem) => {
      const [image, wrapper, button] = stripsItem.children;
      const [headline, subline] = wrapper.children || [];

      return {
        frame: {
          ids: [...acc.frame.ids, stripsItem.id],
          items: [...acc.frame.items, stripsItem],
        },
        image: {
          ids: [...acc.image.ids, image.id],
          items: [...acc.image.items, image],
        },
        headline: {
          ids: [...acc.headline.ids, headline.id],
          items: [...acc.headline.items, headline],
        },
        subline: {
          ids: [...acc.subline.ids, subline.id],
          items: [...acc.subline.items, subline],
        },
        button: {
          ids: [...acc.button.ids, button.id],
          items: [...acc.button.items, button],
        },
      };
    },
    {
      frame: { ids: [] as string[], items: [] as TemplateWithButtonFrame[] },
      image: { ids: [] as string[], items: [] as ImageItem[] },
      headline: {
        ids: [] as string[],
        items: [] as HeadlineItem[],
      },
      subline: {
        ids: [] as string[],
        items: [] as SublineItem[],
      },
      button: {
        ids: [] as string[],
        items: [] as ButtonItem[],
      },
    },
  );

  const handleBulkChange: StyleChildProps['handleBulkChange'] = ({
    element,
    settingKey,
    value,
    values,
  }) => {
    if (element) {
      onChange({
        ids: stripsItems[element].ids,
        settingKey,
        value,
        values,
      });
    }
  };

  const handleFieldsChange: StyleChildProps['handleBulkChange'] = ({
    element,
    settingKey,
    value,
  }) => {
    onChange({
      id: stripsTemplateSlider.id,
      settingKey: `attributes.fields[display${capitalize(element)}]`,
      value: !value,
    });
    handleBulkChange({ element, settingKey, value });
  };

  return (
    <ContentWrapper>
      <AccordionTitle px={0} isInitiallyOpen title={__('Global Settings')}>
        <GlobalSettings
          frames={stripsItems.frame.items}
          handleBulkChange={handleBulkChange}
          item={item}
          onChange={onChange}
          permissions={permissions}
        />
      </AccordionTitle>
      <FieldsDrawer
        item={stripsTemplateSlider.children[0].children}
        onChange={handleFieldsChange}
      />
      <AccordionTitle title={__('Headline')}>
        <Headline
          item={firstHeadline}
          handleBulkChange={({ settingKey, value }) =>
            handleBulkChange({ element: 'headline', settingKey, value })
          }
          onChange={onChange}
          parentItem={item}
          permissions={permissions}
        />
      </AccordionTitle>
      <AccordionTitle title={__('Subline')}>
        <Subline
          item={firstSubline}
          handleBulkChange={({ settingKey, value }) =>
            handleBulkChange({ element: 'subline', settingKey, value })
          }
          onChange={onChange}
          parentItem={item}
          permissions={permissions}
        />
      </AccordionTitle>
      <AccordionTitle title={__('Button')}>
        <Button
          item={firstButton}
          handleBulkChange={({ settingKey, value, values }) =>
            handleBulkChange({ element: 'button', settingKey, value, values })
          }
          parentItem={item}
        />
      </AccordionTitle>
    </ContentWrapper>
  );
};

export default Style;

const ContentWrapper = styled(Box).attrs(() => ({
  flexDirection: 'column',
  width: '100%',
}))``;
